import { useEffect, useMemo, useState } from 'react';
import { Grid } from 'gridjs-react';
import { h } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';
import type { PackageFilters, PackageRecord } from '../types';

interface PackageTableProps {
  data: PackageRecord[];
  isLoading: boolean;
  filters: PackageFilters;
  onFiltersChange: (filters: PackageFilters) => void;
  onEdit: (record: PackageRecord) => void;
  onToggleStatus: (record: PackageRecord) => void;
  onDelete: (record: PackageRecord) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

const currencyFormatter = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0,
});

export function PackageTable({
  data,
  isLoading,
  filters,
  onFiltersChange,
  onEdit,
  onToggleStatus,
  onDelete,
  selectedIds,
  onSelectionChange,
}: PackageTableProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [statusValue, setStatusValue] = useState<PackageFilters['status']>(filters.status ?? 'all');

  useEffect(() => {
    setSearchInput(filters.search ?? '');
  }, [filters.search]);

  useEffect(() => {
    setStatusValue(filters.status ?? 'all');
  }, [filters.status]);

  const recordMap = useMemo(() => new Map(data.map((item) => [item.id, item])), [data]);

  const gridData = useMemo(
    () =>
      data.map((pkg, index) => ({
        checkbox: pkg.id,
        stt: index + 1,
        name: pkg.name,
        code: pkg.code,
        origin_price: pkg.origin_price,
        sale_price: pkg.sale_price,
        functions_count: pkg.functions_count,
        status: pkg.status,
        actions: pkg.id,
      })),
    [data]
  );

  const checkboxHeaderElement = useMemo(() => {
    const allSelected = data.length > 0 && selectedIds.length === data.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;
    const checkboxId = 'packages-select-all-checkbox';

    setTimeout(() => {
      const checkbox = document.getElementById(checkboxId) as HTMLInputElement | null;
      if (checkbox) checkbox.indeterminate = someSelected;
    }, 0);

    return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
      h('input', {
        id: checkboxId,
        type: 'checkbox',
        checked: allSelected,
        onChange: (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange(data.map((item) => item.id));
          } else {
            onSelectionChange([]);
          }
        },
      }),
    ]);
  }, [data, selectedIds, onSelectionChange]);

  const formatText = (value: unknown, maxWidth = 180) => {
    const text = typeof value === 'string' ? value : String(value ?? '—');
    return h(
      'span',
      {
        className: 'truncate-cell',
        title: text,
        style: { maxWidth: `${maxWidth}px`, display: 'inline-block' },
      },
      text
    );
  };

  const formatCheckbox = (cell: unknown) => {
    const recordId = cell as string;
    const record = recordMap.get(recordId);
    if (!record) return null;

    return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
      h('input', {
        type: 'checkbox',
        checked: selectedIds.includes(record.id),
        onChange: (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange([...new Set([...selectedIds, record.id])]);
          } else {
            onSelectionChange(selectedIds.filter((id) => id !== record.id));
          }
        },
      }),
    ]);
  };

  const formatCurrency = (value: unknown) => {
    if (typeof value !== 'number') return formatText(value);
    return h(
      'span',
      {
        style: { fontWeight: 600, color: '#2f3349' },
      },
      currencyFormatter.format(value)
    );
  };

  const formatFunctionsCount = (value: unknown) => {
    const count = typeof value === 'number' ? value : 0;
    return h('span', { className: 'badge bg-info' }, `${count} chức năng`);
  };

  const formatStatus = (value: unknown) => {
    const isActive = value === 'active';
    return h(
      'span',
      {
        className: isActive ? 'badge bg-success' : 'badge bg-secondary',
        style: { minWidth: 72, display: 'inline-block', textAlign: 'center' },
      },
      isActive ? 'Hoạt động' : 'Vô hiệu hóa'
    );
  };

  const formatActions = (cell: unknown) => {
    const record = recordMap.get(cell as string);
    if (!record) return null;
    return h('div', { className: 'table-actions' }, [
      h(
        'button',
        {
          className: 'btn btn-sm',
          title: 'Chỉnh sửa',
          onClick: () => onEdit(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-edit' })
      ),
      h(
        'button',
        {
          className: 'btn btn-sm',
          title: record.status === 'active' ? 'Vô hiệu hóa' : 'Kích hoạt',
          onClick: () => onToggleStatus(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: record.status === 'active' ? 'ti ti-check' : 'ti ti-x' })
      ),
      h(
        'button',
        {
          className: 'btn btn-sm btn-danger',
          title: 'Xóa',
          onClick: () => onDelete(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-trash' })
      ),
    ]);
  };

  const columns = [
    {
      id: 'checkbox',
      name: checkboxHeaderElement,
      formatter: formatCheckbox,
      sort: false,
      width: '60px',
    },
    {
      id: 'stt',
      name: '#',
      formatter: (value: unknown) => h('span', {}, String(value ?? '')),
      sort: false,
      width: '60px',
    },
    { id: 'name', name: 'Tên gói', formatter: (value: unknown) => formatText(value, 220), width: '220px' },
    { id: 'code', name: 'Mã gói', formatter: (value: unknown) => formatText(value, 140), width: '140px' },
    { id: 'origin_price', name: 'Giá gốc', formatter: formatCurrency, width: '150px' },
    { id: 'sale_price', name: 'Giá bán', formatter: formatCurrency, width: '150px' },
    {
      id: 'functions_count',
      name: 'Số chức năng',
      formatter: formatFunctionsCount,
      sort: false,
      width: '150px',
    },
    { id: 'status', name: 'Trạng thái', formatter: formatStatus, sort: false, width: '140px' },
    { id: 'actions', name: 'Hành động', formatter: formatActions, sort: false, width: '160px' },
  ];

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="toolbar-actions" style={{ gap: '12px', flexWrap: 'wrap', alignItems: 'center', display: 'flex' }}>
          <select
            className="status-filter__select"
            value={statusValue}
            onChange={(event) => {
              const nextStatus = event.target.value as PackageFilters['status'];
              setStatusValue(nextStatus);
              onFiltersChange({ ...filters, status: nextStatus, search: searchInput });
            }}
            aria-label="Lọc trạng thái gói"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Vô hiệu hóa</option>
          </select>
          <input
            type="search"
            className="form-control form-control-sm toolbar-input"
            style={{ width: 240 }}
            placeholder="Tìm kiếm tên, mã gói…"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              onFiltersChange({ ...filters, search: event.target.value, status: statusValue });
            }}
          />
        </div>
      </div>

      <div className="vuexy-table-wrapper">
        {isLoading ? (
          <p className="text-muted">Đang tải dữ liệu…</p>
        ) : data.length === 0 ? (
          <div className="card card-body text-center text-muted">Chưa có gói dịch vụ nào.</div>
        ) : (
          <Grid
            data={gridData}
            columns={columns}
            search={false}
            sort
            pagination={{ limit: 10, summary: true }}
            className={{ table: 'vuexy-table' }}
            language={{
              pagination: {
                previous: '‹',
                next: '›',
                showing: 'Hiển thị',
                results: () => 'kết quả',
              },
            }}
          />
        )}
      </div>
    </div>
  );
}


